import { useState, useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "@reduxjs/toolkit";
import { useParams, useNavigate } from "react-router-dom";
import productActions from "../../../store/actions/items/item.actions";
import priceListActions from "../../../store/actions/items/prices/price.actions";
import { SkeletonForm } from "../../../components/general/skeleton";
import useDelayedFlag from "../../../hooks/useDelayedFlag";

export const ManageProductPricePageComponent = ({ priceListActions, productActions }) => {
  const { productId } = useParams(); // Get productId from URL
  const navigate = useNavigate();

  // States
  const [priceLists, setPriceLists] = useState([]); // List of price lists
  const [selectedPriceListId, setSelectedPriceListId] = useState(""); // Selected price list
  const [currentPrice, setCurrentPrice] = useState(""); // Current price for the selected price list
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const showSkeleton = useDelayedFlag(isLoading);

  // Fetch price lists and product prices when the page loads
  useEffect(() => {
    fetchPriceLists();
  }, []);

  useEffect(() => {
    if (selectedPriceListId) {
      fetchCurrentPrice();
    } else {
      setCurrentPrice("");
    }
  }, [selectedPriceListId]);

  const fetchPriceLists = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await priceListActions.getPriceLists({ pageNumber: 1, pageSize: 100 });
      setPriceLists(response.payload.items);
    } catch (error) {
      console.error("Error fetching price lists:", error);
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCurrentPrice = async () => {
    try {
      const productResponse = await productActions.getProductById(productId);
      const productPrices = productResponse.payload.prices || [];

      const priceForSelectedList = productPrices.find((price) => price.priceListID === selectedPriceListId);
      setCurrentPrice(priceForSelectedList ? priceForSelectedList.unitPrice : "");
    } catch (error) {
      console.error("Error fetching product prices:", error);
      setError(error);
    }
  };

  const handlePriceListChange = (event) => {
    setSelectedPriceListId(event.target.value);
  };

  const handlePriceChange = (event) => {
    setCurrentPrice(event.target.value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedPriceListId) {
      alert("Please select a price list.");
      return;
    }

    const priceRequest = {
      productID: productId,
      priceListID: selectedPriceListId,
      unitPrice: parseFloat(currentPrice),
    };

    try {
      await priceListActions.updatePrice(priceRequest);
      alert("Price updated successfully!");
      navigate(`/items`); // Redirect back to product list
    } catch (error) {
      console.error("Error saving price:", error);
      alert("Failed to save price. Please try again.");
    }
  };

  const handleCancel = () => {
    navigate(`/items`);
  };

  return (
    <div>
      <h2>Manage Price for Product</h2>
      <button onClick={handleCancel}>Back to Products</button>

      {isLoading ? (
        showSkeleton && <SkeletonForm fields={2} />
      ) : error ? (
        <p>Error: {error.message || "Something went wrong"}</p>
      ) : (
        <form onSubmit={handleSubmit}>
          <div>
            <label>Price List:</label>
            <select value={selectedPriceListId} onChange={handlePriceListChange} required>
              <option value="">-- Select Price List --</option>
              {priceLists.map((priceList) => (
                <option key={priceList.id} value={priceList.id}>
                  {priceList.name}
                </option>
              ))}
            </select>
          </div>

          {selectedPriceListId && (
            <div>
              <label>Price:</label>
              <input type="number" value={currentPrice} onChange={handlePriceChange} required placeholder="Enter price" min="0" step="0.01" />
            </div>
          )}

          <div>
            <button type="submit">Save Price</button>
            <button type="button" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

const mapDispatchToProps = (dispatch) => {
  return {
    priceListActions: bindActionCreators(priceListActions, dispatch),
    productActions: bindActionCreators(productActions, dispatch),
  };
};

export default connect(null, mapDispatchToProps)(ManageProductPricePageComponent);
